import type { TFormMethods } from "@fluidattacks/design";
import {
  Accordion,
  Container,
  GridContainer,
  TextInput,
} from "@fluidattacks/design";
import _ from "lodash";
import { useEffect, useMemo } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Status } from "../../../formatters/status";
import {
  formatCommitHash,
  formatVulnerabilitiesReason,
  getTimeToDetect,
} from "../../../utils";
import { Detail } from "../../detail";
import { Source } from "../../source";
import type { IFormValues, IGeneralInformationProps } from "../../types";
import { Value } from "../../value";
import { Values } from "../../values";

const GeneralInformationSection: FC<
  IGeneralInformationProps & TFormMethods<IFormValues>
> = ({
  control,
  vulnerability,
  isEditing,
  vulnerabilityAdditionalInfo,
  canSeeSource,
  canRetrieveHacker,
  userEmail,
  commitHash,
  vulnerabilitySource,
  register,
  reset,
}: IGeneralInformationProps & TFormMethods<IFormValues>): JSX.Element => {
  const { t } = useTranslation();

  useEffect((): void => {
    if (
      vulnerabilityAdditionalInfo &&
      vulnerabilityAdditionalInfo.vulnerabilityType === "lines"
    ) {
      const source = vulnerabilityAdditionalInfo.source
        ? vulnerabilityAdditionalInfo.source.toUpperCase()
        : "";

      reset({
        commitHash: vulnerabilityAdditionalInfo.commitHash ?? "",
        source,
        type: vulnerabilityAdditionalInfo.vulnerabilityType,
      });
    }
  }, [vulnerabilityAdditionalInfo, reset]);

  const vulnerabilityType = useMemo((): string => {
    if (vulnerabilityAdditionalInfo === undefined) {
      return t("searchFindings.tabVuln.vulnTable.vulnerabilityType.inputs");
    }

    return t(
      `searchFindings.tabVuln.vulnTable.vulnerabilityType.${vulnerabilityAdditionalInfo.vulnerabilityType}`,
    );
  }, [t, vulnerabilityAdditionalInfo]);

  const closingDate = useMemo((): string | undefined => {
    if (vulnerabilityAdditionalInfo === undefined) {
      return "";
    }

    return (
      vulnerabilityAdditionalInfo.closingDate ??
      vulnerabilityAdditionalInfo.lastStateDate
    )?.split(" ")[0];
  }, [vulnerabilityAdditionalInfo]);

  const canSeeAuthor =
    userEmail.endsWith("@fluidattacks.com") &&
    !_.isNil(vulnerabilityAdditionalInfo?.author) &&
    vulnerabilityAdditionalInfo.vulnerabilityType === "lines";
  const timeToDetect = canSeeAuthor
    ? getTimeToDetect(
        vulnerabilityAdditionalInfo.reportDate,
        vulnerabilityAdditionalInfo.author?.commitDate,
      )
    : undefined;
  const vulnAuthor = canSeeAuthor
    ? vulnerabilityAdditionalInfo.author
    : undefined;

  return (
    <Accordion
      items={[
        {
          description: (
            <GridContainer
              gap={0.75}
              justify={"space-between"}
              lg={2}
              md={2}
              sm={1}
              xl={2}
            >
              <Container>
                <Detail
                  field={<Value value={_.unescape(vulnerability.where)} />}
                  label={t("searchFindings.tabVuln.vulnTable.location")}
                />
                {!_.isEmpty(vulnerability.stream) && (
                  <Detail field={<Value value={vulnerability.stream} />} />
                )}
              </Container>
              <Detail
                field={<Value value={vulnerability.specific} />}
                label={t(
                  `searchFindings.tabVuln.vulnTable.specificType.${vulnerabilityType}`,
                )}
              />
              <Detail
                field={
                  <Value value={vulnerability.reportDate?.split(" ")[0]} />
                }
                label={t("searchFindings.tabVuln.vulnTable.reportDate")}
              />
              <Detail
                field={
                  <Value
                    value={vulnerability.state === "SAFE" ? closingDate : ""}
                  />
                }
                label={t("searchFindings.tabVuln.vulnTable.closingDate")}
              />
              <Detail
                field={
                  <Values
                    values={formatVulnerabilitiesReason(
                      vulnerability.stateReasons,
                    )}
                  />
                }
                label={t("searchFindings.tabDescription.closingReason")}
              />
              <Detail
                editableField={
                  <TextInput name={"commitHash"} register={register} />
                }
                field={<Value value={formatCommitHash(commitHash)} />}
                isEditing={isEditing}
                label={t("searchFindings.tabVuln.commitHash")}
              />
              <Detail
                field={
                  _.isEmpty(vulnerability.zeroRisk) ? (
                    <Value value={undefined} />
                  ) : (
                    <Status status={vulnerability.zeroRisk as string} />
                  )
                }
                label={t("searchFindings.tabDescription.zeroRisk")}
              />
              <Detail
                field={
                  <Value
                    placeHolder={t("searchFindings.tabVuln.notProvided")}
                    value={vulnerability.tag}
                  />
                }
                label={t("searchFindings.tabDescription.tag")}
              />
              {canSeeSource && (
                <Source
                  control={control}
                  isEditing={isEditing}
                  vulnerabilitySource={vulnerabilitySource}
                />
              )}
              {!_.isNil(timeToDetect) && (
                <Detail
                  field={<Value value={timeToDetect.toString()} />}
                  label={t("searchFindings.tabDescription.author.timeToDetect")}
                />
              )}
              {canRetrieveHacker && (
                <Detail
                  field={<Value value={vulnerabilityAdditionalInfo?.hacker} />}
                  label={t("searchFindings.tabDescription.hacker")}
                />
              )}
              {!_.isNil(vulnAuthor) && (
                <Detail
                  field={<Value value={vulnAuthor.authorEmail} />}
                  label={t("searchFindings.tabDescription.author.email")}
                />
              )}
              {!_.isNil(vulnerabilityAdditionalInfo?.methodDescription) && (
                <Detail
                  field={
                    <Value
                      value={vulnerabilityAdditionalInfo.methodDescription}
                    />
                  }
                  label={t(
                    "searchFindings.tabVuln.vulnTable.methodDescription.text",
                  )}
                />
              )}
            </GridContainer>
          ),
          expanded: true,
          title: t("searchFindings.tabVuln.vulnTable.generalInformation"),
        },
      ]}
    />
  );
};

export { GeneralInformationSection };
